import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { createFileLogger } from '../../../utils/logger';
import { entityService } from '../../../services/entityService';
import * as Y from 'yjs';
import { getWebsiteStarterFiles, WebsiteTemplate } from '../services/websiteTemplates';

const log = createFileLogger('WEBSITE_CREATE');

export const websiteCreateCommand: SelfContainedCommand = {
  id: 'website_create',

  description: `Create a new website project that opens in the Builder IDE.

Templates:
- personal   — Personal / portfolio site (index.html, about, styles)
- blog        — Blog with posts listing and individual post pages
- shop-river  — Shop backed by River Product entities + Stripe checkout
- shop-shopify — Shop backed by Shopify Storefront API (no River backend needed)

EXAMPLES:

{ "name": "My Portfolio", "template": "personal", "icon": "User" }
{ "name": "Dev Blog", "template": "blog", "icon": "Newspaper" }
{ "name": "River Store", "template": "shop-river", "description": "Handmade ceramics shop", "icon": "ShoppingBag" }
{ "name": "My Shop", "template": "shop-shopify", "icon": "ShoppingCart" }

**INTEGRATION STRATEGY (FOR SHOPS):**

1. **Call integration_list FIRST** - Know what user has before building
2. **Write code freely** - Use any integration (Stripe, CJDropshipping, etc.)
3. **System detects requirements** - Scans code for API usage patterns
4. **Prompt for missing connections** - Use setup_integration for user-owned APIs
5. **Managed APIs auto-enable** - CJDropshipping, etc. activate on first use

**SHOP WORKFLOW (shop-river template):**

Step 1: integration_list → Know what's available
Step 2: website_create({ template: "shop-river" })
Step 3: setup_integration → Prompt for Stripe if not connected
Step 4: api_connect({ managedApiId: "managed_cjdropshipping" }) → Auto-enables
Step 5: product_create → Add products (auto-links via websiteId)
Step 6: entity_create → Create EntityTrigger for order automation
Step 7: Claim subdomain for live deployment

AFTER CREATING:
- For shop-river: use product_create to add Product entities (auto-links via websiteId)
- Products can be sourced from CJDropshipping (managed API)
- Create EntityTrigger for auto-fulfillment on purchase
- For shop-shopify: edit src/shopify-config.js with the store domain + storefront token
- Use code_write to customise pages, colours, and copy
`,

  autonomyLevel: 'auto',
  visualTreatment: 'narrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Website name (e.g., "My Portfolio", "Artisan Ceramics")',
        required: true,
        validator: (value: string) => {
          if (!value || typeof value !== 'string') return 'Name is required';
          if (value.trim().length < 2) return 'Name must be at least 2 characters';
          return null;
        },
      },
      {
        name: 'template',
        type: 'string',
        description: 'Template: personal | blog | shop-river | shop-shopify',
        required: false,
        validator: (value: string) => {
          const valid = ['personal', 'blog', 'shop-river', 'shop-shopify'];
          if (value && !valid.includes(value)) {
            return `Template must be one of: ${valid.join(', ')}`;
          }
          return null;
        },
      },
      {
        name: 'description',
        type: 'string',
        description: 'Short description (used in SEO meta tags and hero text)',
        required: false,
      },
      {
        name: 'icon',
        type: 'string',
        description: 'Phosphor icon name matching the site\'s purpose (e.g. "Globe", "ShoppingBag", "Newspaper", "User"). Defaults to "Globe" if not specified.',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'My Portfolio', template: 'personal', icon: 'User' },
        expectedOutput: createSuccessResult('Created "My Portfolio" website', {}),
        description: 'Personal portfolio site',
        context: 'User wants a personal site',
      },
      {
        input: { name: 'River Ceramics', template: 'shop-river', description: 'Handmade ceramics shipped worldwide', icon: 'ShoppingBag' },
        expectedOutput: createSuccessResult('Created "River Ceramics" website', {}),
        description: 'Shop backed by River + Stripe',
        context: 'User wants to sell products',
      },
    ],
    bad: [],
  },

  performance: { expectedDuration: 1200, complexity: 'low' },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { name, template = 'personal', description = '', icon } = params;
    const resolvedTemplate = (template || 'personal') as WebsiteTemplate;

    log.info('[website_create] Creating website:', { name, template: resolvedTemplate, description });

    try {
      // Build Y.js document with starter files
      const ydoc = new Y.Doc();
      const filesMap = ydoc.getMap('files');

      const starterFiles = getWebsiteStarterFiles(name, resolvedTemplate, description);
      for (const [path, content] of Object.entries(starterFiles)) {
        const ytext = new Y.Text();
        ytext.insert(0, content);
        filesMap.set(path, ytext);
      }

      // Encode Y.js state to base64
      const ystate = Y.encodeStateAsUpdate(ydoc);
      ydoc.destroy();
      let binary = '';
      const bytes = new Uint8Array(ystate);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      // Create website_project entity
      const projectEntity = await entityService.create({
        entityType: 'website_project',
        name,
        icon: icon ?? 'Globe',
        spaceId: context.spaceId,
        skeleton: {
          '@type': 'website_project',
          version: '1',
          architecture: 'yjs',
          base64,
          description,
          projectKind: 'website',
          template: resolvedTemplate,
          createdBy: 'website_create',
          createdAt: new Date().toISOString(),
        },
      });

      log.info('[website_create] Entity created:', projectEntity._id);

      // Open in builder
      const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
      const { executeCommand } = useCommandRegistryStore.getState();
      const windowResult = (await executeCommand('space_open_window', { entityId: projectEntity._id })) as StandardToolResult;
      const windowId = windowResult.data?.windowId;

      const duration = Date.now() - startTime;
      const fileCount = Object.keys(starterFiles).length;

      const nextSteps: string[] = [];
      if (resolvedTemplate === 'shop-river') {
        nextSteps.push('Create Product entities: river.entity.create({ entityType: "Product", name: "...", skeleton: { price: 2500, currency: "usd", inStock: true } })');
        nextSteps.push('Set stripePriceId on each product after creating prices in Stripe Dashboard');
        nextSteps.push('Set STRIPE_SECRET_KEY environment variable when deploying');
      } else if (resolvedTemplate === 'shop-shopify') {
        nextSteps.push('Edit src/shopify-config.js — set SHOPIFY_DOMAIN and SHOPIFY_STOREFRONT_TOKEN');
      }
      nextSteps.push('Use code_write to customise pages, colours, and copy');

      return createSuccessResult(
        `Created "${name}" website (${resolvedTemplate} template) — ${fileCount} files ready`,
        {
          entityId: projectEntity._id,
          windowId,
          name,
          template: resolvedTemplate,
          fileCount,
          files: Object.keys(starterFiles),
          nextSteps,
        },
        { metadata: { executionTime: duration }, ui: { autoFocus: windowId, refreshContext: true } }
      );
    } catch (error: any) {
      log.error('[website_create] Error:', error);
      return createErrorResult('Failed to create website project', error.message || 'Unknown error', 'execution');
    }
  },
};
